import React, { useState } from 'react';
import { ERC3643Info } from '../contracts/contract';
import './AgentOperations.css';

interface AgentOperationsProps {
  tokenInfo: ERC3643Info | null;
  onMint: (to: string, amount: string) => Promise<void>;
  onBurn: (address: string, amount: string) => Promise<void>;
  onFreezeAddress: (address: string, freeze: boolean) => Promise<void>;
  onFreezePartial: (address: string, amount: string) => Promise<void>;
  onUnfreezePartial: (address: string, amount: string) => Promise<void>;
  onPauseToggle: () => Promise<void>;
  loading: boolean;
}

const AgentOperations: React.FC<AgentOperationsProps> = ({
  tokenInfo,
  onMint,
  onBurn,
  onFreezeAddress,
  onFreezePartial,
  onUnfreezePartial,
  onPauseToggle,
  loading
}) => {
  // Form states
  const [mintTo, setMintTo] = useState('');
  const [mintAmount, setMintAmount] = useState('');
  const [burnAddress, setBurnAddress] = useState('');
  const [burnAmount, setBurnAmount] = useState('');
  const [freezeAddress, setFreezeAddress] = useState('');
  const [freezeAmount, setFreezeAmount] = useState('');
  const [unfreezeAddress, setUnfreezeAddress] = useState('');
  const [unfreezeAmount, setUnfreezeAmount] = useState('');

  const handleMint = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mintTo || !mintAmount) return;
    
    try {
      await onMint(mintTo, mintAmount);
      setMintTo('');
      setMintAmount('');
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleBurn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!burnAddress || !burnAmount) return;
    
    try {
      await onBurn(burnAddress, burnAmount);
      setBurnAddress('');
      setBurnAmount('');
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleFreezeAddress = async (freeze: boolean) => {
    if (!freezeAddress) return;
    
    try {
      await onFreezeAddress(freezeAddress, freeze);
      setFreezeAddress('');
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleFreezePartial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!freezeAddress || !freezeAmount) return;
    
    try {
      await onFreezePartial(freezeAddress, freezeAmount);
      setFreezeAddress('');
      setFreezeAmount('');
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleUnfreezePartial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!unfreezeAddress || !unfreezeAmount) return;
    
    try {
      await onUnfreezePartial(unfreezeAddress, unfreezeAmount);
      setUnfreezeAddress('');
      setUnfreezeAmount('');
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  return (
    <div className="agent-operations">
      <h3>Agent Operations</h3>
      
      <div className="operations-grid">
        {/* Mint Form */}
        <div className="operation-card">
        <h4>Mint Tokens</h4>
        <form onSubmit={handleMint} className="operation-form">
          <div className="form-fields">
            <div className="form-group">
              <label htmlFor="mint-to">To Address:</label>
              <input
                id="mint-to"
                type="text"
                value={mintTo}
                onChange={(e) => setMintTo(e.target.value)}
                placeholder="0x..."
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="mint-amount">Amount:</label>
              <input
                id="mint-amount"
                type="number"
                step="0.000001"
                value={mintAmount}
                onChange={(e) => setMintAmount(e.target.value)}
                placeholder="0.0"
                required
              />
            </div>
          </div>
          <div className="button-container">
            <button type="submit" className="action-btn mint-btn" disabled={loading}>
              Mint
            </button>
          </div>
        </form>
      </div>

      {/* Burn Form */}
      <div className="operation-card">
        <h4>Burn Tokens</h4>
        <form onSubmit={handleBurn} className="operation-form">
          <div className="form-fields">
            <div className="form-group">
              <label htmlFor="burn-address">Address:</label>
              <input
                id="burn-address"
                type="text"
                value={burnAddress}
                onChange={(e) => setBurnAddress(e.target.value)}
                placeholder="0x..."
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="burn-amount">Amount:</label>
              <input
                id="burn-amount"
                type="number"
                step="0.000001"
                value={burnAmount}
                onChange={(e) => setBurnAmount(e.target.value)}
                placeholder="0.0"
                required
              />
            </div>
          </div>
          <div className="button-container">
            <button type="submit" className="action-btn burn-btn" disabled={loading}>
              Burn
            </button>
          </div>
        </form>
      </div>

      {/* Freeze Address */}
      <div className="operation-card">
        <h4>Freeze/Unfreeze Address</h4>
        <div className="operation-form">
          <div className="form-fields">
            <div className="form-group">
              <label htmlFor="freeze-address">Address:</label>
              <input
                id="freeze-address"
                type="text"
                value={freezeAddress}
                onChange={(e) => setFreezeAddress(e.target.value)}
                placeholder="0x..."
                required
              />
            </div>
          </div>
          <div className="button-container">
            <div className="button-group">
              <button 
                type="button" 
                className="action-btn freeze-btn" 
                onClick={() => handleFreezeAddress(true)}
                disabled={loading}
              >
                Freeze
              </button>
              <button 
                type="button" 
                className="action-btn unfreeze-btn" 
                onClick={() => handleFreezeAddress(false)}
                disabled={loading}
              >
                Unfreeze
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Freeze Partial Tokens */}
      <div className="operation-card">
        <h4>Freeze Partial Tokens</h4>
        <form onSubmit={handleFreezePartial} className="operation-form">
          <div className="form-fields">
            <div className="form-group">
              <label htmlFor="freeze-partial-address">Address:</label>
              <input
                id="freeze-partial-address"
                type="text"
                value={freezeAddress}
                onChange={(e) => setFreezeAddress(e.target.value)}
                placeholder="0x..."
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="freeze-partial-amount">Amount:</label>
              <input
                id="freeze-partial-amount"
                type="number"
                step="0.000001"
                value={freezeAmount}
                onChange={(e) => setFreezeAmount(e.target.value)}
                placeholder="0.0"
                required
              />
            </div>
          </div>
          <div className="button-container">
            <button type="submit" className="action-btn freeze-partial-btn" disabled={loading}>
              Freeze Partial
            </button>
          </div>
        </form>
      </div>

      {/* Unfreeze Partial Tokens */}
      <div className="operation-card">
        <h4>Unfreeze Partial Tokens</h4>
        <form onSubmit={handleUnfreezePartial} className="operation-form">
          <div className="form-fields">
            <div className="form-group">
              <label htmlFor="unfreeze-partial-address">Address:</label>
              <input
                id="unfreeze-partial-address"
                type="text"
                value={unfreezeAddress}
                onChange={(e) => setUnfreezeAddress(e.target.value)}
                placeholder="0x..."
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="unfreeze-partial-amount">Amount:</label>
              <input
                id="unfreeze-partial-amount"
                type="number"
                step="0.000001"
                value={unfreezeAmount}
                onChange={(e) => setUnfreezeAmount(e.target.value)}
                placeholder="0.0"
                required
              />
            </div>
          </div>
          <div className="button-container">
            <button type="submit" className="action-btn unfreeze-partial-btn" disabled={loading}>
              Unfreeze Partial
            </button>
          </div>
        </form>
      </div>

      {/* Pause/Unpause */}
      <div className="operation-card">
        <h4>Token Control</h4>
        <div className="operation-form">
          <div className="form-fields">
            {/* Empty form fields to maintain consistent spacing */}
          </div>
          <div className="button-container">
            <button 
              type="button" 
              className={`action-btn ${tokenInfo?.isPaused ? 'unpause-btn' : 'pause-btn'}`}
              onClick={onPauseToggle}
              disabled={loading}
            >
              {tokenInfo?.isPaused ? 'Unpause Token' : 'Pause Token'}
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default AgentOperations;
